import devHours from "../../components/industries/sections/devHours"

/**
 * /industries/distribution data — rebuilt from
 * src/Design/industries/industry-distribution.html, word for word.
 *
 * Content-to-verify: "Check features and FAQ answers against the D365
 * Support & Continuing Development offer page. Never claim 24/7." Checked
 * against src/data/offers.js's dynamics-365-support: named engineer,
 * regression checks against Microsoft updates, continuing development and
 * "eight hours a day" business-hours coverage all match `included`/
 * `notIncluded` exactly, and that offer's own note explicitly confirms
 * coverage is business hours, NOT 24/7 — no 24/7 claim appears anywhere
 * below. "253k Development hours" uses devHours() (the same live, ticking
 * figure the homepage hero uses), not a hardcoded snapshot.
 *
 * Title tag and meta description are unchanged from the previous version of
 * this page.
 */
const distribution = ({ heroImage, challengeImages = [], solutionImage, resultsImage }) => ({
  slug: "distribution",
  industryName: "Distribution",

  kicker: "Solutions for distributors managing stock across locations",
  headline: "One accurate stock picture across every location",
  subheading:
    "Whether you run Dynamics 365, an ERP that isn't keeping up, or systems that don't talk to each other — we get stock, orders and transfers agreeing across every location.",
  heroImage,
  heroImageAlt: "Distribution work in progress",
  ctaHref: "/contact",
  ctaLabel: "Book your free consultation",
  badge: "Hush Puppies · bulk stock transfers to many stores at once",
  stats: [
    { value: "25+", label: "Reports built for Borjan" },
    { value: "3 brands", label: "Supported on Dynamics 365" },
    { value: "35+", label: "Solutions delivered" },
    { value: `${devHours()}`, label: "Development hours" },
  ],

  challengesEyebrow: "What slows distribution teams down",
  challengesHeading: "Why distribution systems stop keeping up",
  challengesIntro: "The system went live years ago. The business kept changing.",
  challenges: [
    {
      image: challengeImages[0],
      heading: "Implemented once, never touched",
      detail: "Your D365 went live and hasn't been improved since.",
    },
    {
      image: challengeImages[1],
      heading: "Customisations nobody understands",
      detail: "The people who built them left, and the knowledge went with them.",
    },
    {
      image: challengeImages[2],
      heading: "Small fixes take weeks",
      detail: "Without a dedicated support relationship, every change joins a queue.",
    },
    {
      image: challengeImages[3],
      heading: "Agency rates for small jobs",
      detail: "You pay big-agency retainers for changes that take an afternoon.",
    },
  ],

  situationsEyebrow: "How we help distribution teams",
  situationsHeading: "Wherever you're starting from",
  situationsIntro:
    "Distributors come to us with very different systems. These are the four situations we see most, and how we handle each.",
  situations: [
    {
      tag: "Already on Dynamics 365",
      title: "Keep it accurate and improving",
      detail: "A named engineer who knows your setup looks after it, 160 business hours a month.",
      ghostIconPath: '<path d="M20 11a8 8 0 0 0-14.8-3.5M4 4v4h4"/><path d="M4 13a8 8 0 0 0 14.8 3.5M20 20v-4h-4"/>',
      footerText: "Dynamics 365 support",
      footerHref: "/offers/dynamics-365-support",
    },
    {
      tag: "ERP not keeping up",
      title: "Fix it before you replace it",
      detail: "We find what's holding your current ERP back and get it working again.",
      ghostIconPath:
        '<path d="M7 10h3V7L6.5 3.5a6 6 0 0 1 8 8l6 6a2 2 0 0 1-3 3l-6-6a6 6 0 0 1-8-8L7 10"/>',
      footerText: "ERP Rescue Sprint",
      footerHref: "/offers/erp-rescue-sprint",
    },
    {
      tag: "Moving to a new system",
      title: "Start clean on Odoo",
      detail: "We implement Odoo with your warehouses, transfers and orders set up from the start.",
      ghostIconPath:
        '<path d="M5 15c-1 1-1.5 3.5-1.5 5.5 2 0 4.5-.5 5.5-1.5"/><path d="M9 15l-3-3c1.5-4 5-8 12-8 0 7-4 10.5-8 12z"/><circle cx="14.5" cy="9.5" r="1.5"/>',
      footerText: "Odoo implementation",
      footerHref: "/Odoo/implementationPartner",
    },
    {
      tag: "Systems that don't connect",
      title: "Stock updated once, everywhere",
      detail: "We connect your ERP, storefront, marketplaces and warehouse so every channel sees the same numbers.",
      ghostIconPath:
        '<path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1"/><path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1"/>',
      footerText: "Talk to us",
      footerHref: "/contact",
    },
  ],
  situationsUnsureHeading: "Not sure which fits?",
  situationsUnsureDetail: "The Fit Sprint gives you a costed plan before you commit to anything.",
  situationsUnsureLinkLabel: "See the Fit Sprint",
  situationsUnsureLinkHref: "/offers/ops-and-erp-fit-sprint",

  resultsEyebrow: "Results for distribution clients",
  resultsHeading: "Stock that stays right across locations",
  resultsMetrics: [
    { value: "Minutes", label: "to run Borjan's supply chain and finance reports, which used to take a week to prepare" },
    { value: "3", label: "distribution brands supported on Dynamics 365" },
  ],
  resultsSummary:
    "Xstak and Dynamics 365 stay in sync for Hush Puppies, and Cakes & Bakes' production facility now records every dispatch in the system.",
  resultsLinks: [
    { label: "Borjan", href: "/caseStudies/projects/borjan" },
    { label: "Hush Puppies", href: "/caseStudies/projects/hushPuppiesPakistan" },
    { label: "Cakes & Bakes", href: "/caseStudies/projects/cakesAndBakes" },
  ],
  resultsImage,
  resultsImageAlt: "Borjan · Hush Puppies · Cakes & Bakes",

  // Names resolved against src/data/clientLogos.js by TrustedBy.js.
  trustedByLogos: ["Borjan", "Hush Puppies", "Cakes & Bakes", "Sohar Port & Freezone"],

  whyEyebrow: "Why Visiomate for distribution",
  whyHeading: "What you get with this team",
  whyIntro: "Three things that stay the same on every engagement.",
  whyLinkHref: "/contact",
  reasons: [
    {
      title: "Same team, start to finish",
      detail: "The people who start your project finish it, with the founder involved throughout.",
    },
    { title: "30-day warranty", detail: "Everything we configure or build is covered for 30 days after go-live." },
    {
      title: "We pick what fits your setup",
      detail: "Odoo, Dynamics 365 or a custom build — we recommend what suits your business, not one product.",
    },
  ],

  questionsEyebrow: "Questions",
  questionsHeading: "Questions buyers ask before they commit",
  questionsIntro: "Something else on your mind?",
  questionsLinkHref: "/contact",
  faqs: [
    {
      q: "We already have Dynamics 365 — do we have to replace it?",
      a: "No. A named engineer can maintain and extend it, 160 business hours a month. Cover is business hours, not 24/7.",
    },
    {
      q: "Our ERP isn't keeping up — should we fix it or replace it?",
      a: "The ERP Rescue Sprint finds what's holding it back, so you decide on facts before spending on a replacement.",
    },
    {
      q: "Can you connect our ERP, storefront and marketplaces?",
      a: "Yes. We connect them so stock and orders are updated once and every channel sees the same numbers.",
    },
    {
      q: "How do we start if we're not sure what we need?",
      a: "With the Fit Sprint: we map how you work and give you a costed plan before you commit to anything.",
    },
  ],

  ctaHeading: "Start with a costed roadmap, not a proposal",
  ctaDetail: "The Ops & ERP Fit Sprint gives you a costed plan before you commit to anything larger.",
  ctaLinkLabel: "See the Fit Sprint",
  ctaLinkHref: "/offers/ops-and-erp-fit-sprint",

  metaTitle: "Distribution Software Development Company | Visiomate",
  metaDescription:
    "Dynamics 365 support and continuing development for distribution operations already live on D365 — a named engineer, business-hours cover, 160 hours a month.",
})

export default distribution
